import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useCallback, useEffect } from "react";

import {
  generalParametersState,
  selectedValueConfigState,
  subProductsCopyParametersState,
  subProductsParametersState
} from "@/store";

import { selectColorValueState } from "./store/selecte-color-value";
import { useTranslation } from "react-i18next";
import lodashClonedeep from "lodash.clonedeep";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { useRouter } from "next/router";
import { ProductLogActionEnum } from "@/enums/products";
import {
  COLOR_DETAIL_CODES,
  findSettingParamByCodes,
  validatePrintingColorSelection,
  getPrintingColorSideCodes,
  findPrintingColorSubProductRow,
  mirrorColorLoadAverageInSubProductParameters,
  hasCompleteSelectedColorValues,
  getPrintingColorRowValueList,
} from "./printing-colors-preset-utils";

const useMultiParameterModal = ({ settingParameters, onClose,isParameterSettings ,getProductById,setCanCalculation}) => {
  const [generalParameters, setGeneralParameters] = useRecoilState<any>(
    generalParametersState
  );
  const subProductsParameters = useRecoilValue(subProductsParametersState);
  const { callApi } = useGomakeAxios();
  const { alertFaultAdded, alertSuccessAdded, alertFault } = useSnackBar();
  const router = useRouter();
  const { t } = useTranslation();
  const parameterLists = settingParameters?.parameter?.settingParameters;
  const selectedValueConfig = useRecoilValue(selectedValueConfigState);
  const setSelectColorValue = useSetRecoilState(selectColorValueState);
  const [subProducts, setSubProducts] = useRecoilState<any>(subProductsParametersState);
  const [subProductsCopy, setSubProductsCopy] = useRecoilState<any>(subProductsCopyParametersState);


  const getObjectById = () => {
    for (const config of selectedValueConfig) {
      let foundParameter = generalParameters.find(
        (param) => param && param.valueIds && param.valueIds[0] === config.id
      );
      if(!foundParameter){
        const subProduct = subProductsParameters.find(subProduct => settingParameters.section && subProduct.sectionId === settingParameters.section.id );
        if(subProduct && subProduct.parameters && subProduct.parameters.length > 0){
          foundParameter = subProduct?.parameters?.find(
              (param) => param && param.valueIds && param.valueIds[0] === config.id
          );
        }
      }
      if (foundParameter) {
        return config;
      }
    }
  };
  useEffect(() => {
    const result = getObjectById();
    setSelectColorValue(result);
  }, [generalParameters, selectedValueConfig,subProducts]);

  const onClickSaveParameter = () => {
    const valueConfig = getObjectById();
    const settingParamsList =
      settingParameters?.parameter?.settingParameters ?? [];
    const subSectionType = settingParameters?.subSection?.type;
    const sectionId = settingParameters?.section?.id;
    const subSectionId = settingParameters?.subSection?.id;
    const actionIndex = settingParameters?.parameter?.actionIndex;
    const currentParamCode: string = settingParameters?.parameter?.code ?? "";
    const isSide2 =
      typeof currentParamCode === "string" &&
      currentParamCode.toLowerCase().includes("side2");
    const colorParamCode = isSide2
      ? "printingcolorsDetailSide2"
      : "printingcolorsDetail";

    if (valueConfig) {
      const target = subProductsCopy?.find(
        (sp: any) => sp?.type === subSectionType
      );
      const printingcolorsDetail = findSettingParamByCodes(
        settingParamsList,
        COLOR_DETAIL_CODES
      );
      let colorParamVal = target?.parameters?.find(
        (p: any) =>
          p.parameterId === printingcolorsDetail?.id &&
          p.sectionId === sectionId &&
          p.subSectionId === subSectionId &&
          (typeof actionIndex === "number" ? p.actionIndex === actionIndex : true)
      );
      if (!colorParamVal) {
        colorParamVal = target?.parameters?.find(
          (p: any) =>
            p.parameterCode === colorParamCode &&
            p.sectionId === sectionId &&
            p.subSectionId === subSectionId &&
            (typeof actionIndex === "number" ? p.actionIndex === actionIndex : true)
        );
      }

      const validation = validatePrintingColorSelection(
        valueConfig,
        settingParamsList,
        colorParamVal?.valueIds ?? []
      );
      if (!validation.valid) {
        alertFault(
          t(validation.messageKey ?? "products.offsetPrice.admin.errorReq", {
            ...(validation.messageParams ?? {}),
          })
        );
        return;
      }
    }

    setCanCalculation(true)
    let copy = lodashClonedeep(subProductsCopy);

    // Keep per-color load/layers. Fill missing loads from average, but never overwrite Color load average from individual loads.
    try {
      const subSectionType = settingParameters?.subSection?.type;
      const target = copy?.find((sp: any) => sp?.type === subSectionType);
      if (target && Array.isArray(target.parameters)) {
        const currentParamCode: string = settingParameters?.parameter?.code;
        const isSide2 =
          typeof currentParamCode === "string" &&
          currentParamCode.toLowerCase().includes("side2");
        const sideCodes = getPrintingColorSideCodes(isSide2);
        const sectionId = settingParameters?.section?.id;
        const subSectionId = settingParameters?.subSection?.id;
        const actionIndex = settingParameters?.parameter?.actionIndex;
        const settingParamsList =
          settingParameters?.parameter?.settingParameters ?? [];
        const scope = { sectionId, subSectionId, actionIndex };

        const avgRow = findPrintingColorSubProductRow(target.parameters, {
          role: "average",
          isSide2,
          settingParamsList,
          ...scope,
        });
        const savedAvg = avgRow?.values?.[0] ?? avgRow?.valueIds?.[0];

        const colorParamVal = findPrintingColorSubProductRow(target.parameters, {
          role: "color",
          isSide2,
          settingParamsList,
          ...scope,
        });
        const loadParamVal = findPrintingColorSubProductRow(target.parameters, {
          role: "load",
          isSide2,
          settingParamsList,
          ...scope,
        });

        if (
          !hasCompleteSelectedColorValues(colorParamVal, loadParamVal) &&
          savedAvg != null &&
          savedAvg !== ""
        ) {
          target.parameters = mirrorColorLoadAverageInSubProductParameters(
            target.parameters,
            {
              ...scope,
              loadFillValue: savedAvg,
              isSide2,
              settingParamsList,
            }
          );
        }

        const persistSelectedPair = (role: "load" | "layers", code: string) => {
          if (!colorParamVal?.valueIds?.length) return;
          const entry = findPrintingColorSubProductRow(target.parameters, {
            role,
            isSide2,
            settingParamsList,
            ...scope,
          });
          if (!entry) return;
          const idx = target.parameters.indexOf(entry);
          if (idx === -1) return;
          const selectedCount = colorParamVal.valueIds.length;
          const normalized = getPrintingColorRowValueList(entry)
            .slice(0, selectedCount)
            .map((v: any) => String(v));
          if (!normalized.length) return;
          target.parameters[idx] = {
            ...target.parameters[idx],
            parameterCode: code,
            values: normalized,
            valueIds: normalized,
          };
        };

        persistSelectedPair("load", sideCodes.load);
        persistSelectedPair("layers", sideCodes.layers);
      }
    } catch (err) {
      // ignore load/layers persist failure - do not block save
    }

    setSubProducts(copy)
    setSubProductsCopy(copy)
    onClose();

    
  };
  const updatedValuesConfigsForParameters = useCallback(
    async (sectionId: string, subSectionId: string, data: any) => {
      const rawId = router?.query?.id;
      const entityId = Array.isArray(rawId) ? rawId[0] : rawId;
      const isTemplateEdit = router.asPath?.includes("/settings/templates/");
      const res = await callApi(
        "PUT",
        isTemplateEdit
          ? `/v1/printhouse-config/templates/update-template-parameter`
          : `/v1/printhouse-config/products/update-product-parameter`,
        {
          productId: entityId,
          sectionId: sectionId,
          subSectionId: subSectionId,
          productParameterType: 1,
          actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
          parameter: data,
        }
      );
      if (res?.success) {
        alertSuccessAdded()
      } else {
        alertFaultAdded()
      }
    },
    [router]
  );

  const updatedProductParameteDefaultValue = useCallback(
    async (sectionId: string, subSectionId: string, data: any) => {
        await updatedValuesConfigsForParameters(sectionId,subSectionId,data);
    },

    [router]
  );
  const onClickSaveParameterForSettings = (sectionId,subSectionId,data) => {
    updatedProductParameteDefaultValue(sectionId,subSectionId,data)
    getProductById()
    onClose();

    
  };
  const closeModal = () => {
   
    onClose();
  }
  return {
    parameterLists,
    onClickSaveParameter,
    closeModal,
    onClickSaveParameterForSettings,
    t,
  };
};

export { useMultiParameterModal };
